import {gsap} from 'gsap'
import {ScrollTrigger} from 'gsap/all'
import * as React from 'react'
import {render} from 'storyblok-rich-text-react-renderer'

import {DEFAULT_LOCALE} from '@/common/config'
import {isEmptyRichtext, makeEditable, tailwindConfig} from '@/common/utils'
import DynamicIcon from '@/components/DynamicIcon'
import {Grid} from '@/components/Grid'
import Image from '@/components/Image'
import Link from '@/components/Link'
import RichText from '@/components/RichText/Richtext'

import TableOfContent from './TableOfContent'

const SocialMedias = React.lazy(() => import('./SocialMedias'))

const formatDate = (date, locale) => {
  locale = locale || DEFAULT_LOCALE
  if (date) {
    return new Date(date).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }
}

const Article = ({heading, richtext, story, _editable}) => {
  const {cover = {}, author, title, date, category = {}, lang} = story

  const sectionRef = React.useRef()
  const contentRef = React.useRef()

  React.useEffect(() => {
    const section = sectionRef.current
    const content = contentRef.current ? contentRef.current.children[0] : null
    if (section && content) {
      const sectionPaddingBottom = gsap.getProperty(section, 'paddingBottom')
      const mm = gsap.matchMedia()
      mm.add(`(min-width: ${tailwindConfig.theme.screens['lg']})`, () => {
        ScrollTrigger.create({
          pin: content,
          pinSpacing: false,
          pinType: 'transform',
          trigger: content,
          endTrigger: section,
          start: 'top top+=24px',
          end: `bottom-=${sectionPaddingBottom}px center`,
        })
      })
      return () => mm.revert()
    }
  }, [])

  return (
    <section ref={sectionRef} {...makeEditable(_editable)}>
      <Grid className='relative pb-37 lg:pb-57 lg:pt-10'>
        <div
          className='relative inset-0 z-30 col-span-full my-6  md:my-8 lg:col-span-4 lg:my-0 lg:pr-18'
          ref={contentRef}
        >
          <div className='flex flex-col gap-2'>
            <TableOfContent richtext={richtext} />
            <SocialMedias />
          </div>
        </div>
        <div className='col-span-full space-y-8 md:space-y-20 lg:col-start-5'>
          <div className='prose max-w-none'>
            <h1 className='heading-m-satoshi mb-7 text-purple-900'>
              {!isEmptyRichtext(heading) ? render(heading) : title}
            </h1>
            <div className='misc-button-s flex items-center'>
              {category && (
                <span className='rounded-2xl border px-[10px] py-[2px]'>{category.name}</span>
              )}
              {date && (
                <>
                  <span className='px-4'>•</span>
                  <span>{formatDate(date, lang)}</span>
                </>
              )}
            </div>
          </div>
          {cover.filename && (
            <div className='aspect-h-9 aspect-w-16'>
              <Image
                src={cover.filename}
                alt={cover.alt}
                className='rounded-2xl object-cover'
                sizes={{sm: '344', md: '672', lg: '800', unit: 'px'}}
              />
            </div>
          )}

          <RichText richtext={richtext} title={title} />
          {author && (
            <div className='flex flex-col gap-8'>
              <div className='flex items-center justify-between rounded-[40px] bg-yellow-200 p-6'>
                <div className='flex items-center gap-6'>
                  <Image
                    {...author.content.image}
                    ratio='1:1'
                    sizes={{sm: '40', unit: 'px'}}
                    className='h-20 w-20 rounded-full'
                    alt=''
                  />
                  <div className='flex flex-col gap-4'>
                    <Link link={author.content.authorPage} className='body-xl-bold'>
                      {author.name}
                    </Link>
                    <span className='body-m-medium'>
                      {author.content.company} - {author.content.job}
                    </span>
                  </div>
                </div>
                {author.content.linkedin.url && (
                  <Link link={author.content.linkedin} className='rounded-2xl bg-yellow-300 p-4'>
                    <DynamicIcon aria-hidden icon={'IconLinkedin'} />
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </Grid>
    </section>
  )
}

export default Article
